import type { FinancialAidStaffDetailContext } from '../ApplicantDetail';
import { asArray, asRecord } from '../../utils/domainData';

export const PARENT_FINANCIAL_AID_RAW_SNAPSHOT_KEY = 'parentCollectedFinancialAidSnapshot';

export const readParentCollectedFinancialAidSnapshot = (
  review: FinancialAidStaffDetailContext['review'],
): Record<string, unknown> => {
  const reviewRecord = asRecord(review as unknown);
  const application = asRecord(reviewRecord.application);
  const sections = asRecord(application.sections);
  const additionalInfo = asRecord(sections.additionalInfo);
  return asRecord(additionalInfo[PARENT_FINANCIAL_AID_RAW_SNAPSHOT_KEY]);
};

export const readSnapshotRecord = (
  record: Record<string, unknown>,
  key: string,
): Record<string, unknown> => asRecord(record[key]);

export const readSnapshotArray = (
  record: Record<string, unknown>,
  key: string,
): Record<string, unknown>[] => asArray<Record<string, unknown>>(record[key]);

export const readRawText = (value: unknown, fallback = ''): string => {
  const next = String(value ?? '').trim();
  return next || fallback;
};

export const readRawAmount = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const normalized = String(value ?? '').replace(/[$,\s]/g, '');
  if (!normalized) return 0;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const readRawBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  return /^(true|yes|y|1)$/i.test(readRawText(value));
};

export const firstNonEmptyRawText = (...values: unknown[]): string => {
  for (const value of values) {
    const text = readRawText(value);
    if (text) return text;
  }
  return '';
};

export type ParentFinancialAidSnapshotKey = 'father' | 'mother';

export const readParentFinancialAidRelationshipKey = (
  relationship: unknown,
): ParentFinancialAidSnapshotKey | null => {
  const normalized = readRawText(relationship).toLowerCase().replace(/[\s_-]+/g, '');
  if (['father', 'dad', 'stepfather', 'stepdad', 'parent1', 'guardian1'].includes(normalized)) return 'father';
  if (['mother', 'mom', 'stepmother', 'stepmom', 'parent2', 'guardian2'].includes(normalized)) return 'mother';
  return null;
};

export const readParentFinancialAidSnapshotKey = (
  relationship: unknown,
  fallback: ParentFinancialAidSnapshotKey,
): ParentFinancialAidSnapshotKey =>
  readParentFinancialAidRelationshipKey(relationship) ?? fallback;

export const readParentSnapshotTextFallback = (
  parent: Record<string, unknown>,
  snapshotParent: Record<string, unknown>,
  fieldName: string,
  fallback = '',
): string =>
  firstNonEmptyRawText(parent[fieldName], snapshotParent[fieldName]) || fallback;

export const describeRawAmountWithDetails = (
  amount: unknown,
  details: unknown,
  fallback: string | null = null,
): string | null => {
  const numericAmount = readRawAmount(amount);
  const text = readRawText(details);
  if (numericAmount <= 0 && !text) return fallback;

  const formattedAmount = numericAmount > 0
    ? numericAmount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    : '';

  if (formattedAmount && text) return `${formattedAmount}: ${text}`;
  return formattedAmount || text || fallback;
};
